/**
 * The system's OWN attributes: the ones that do not roll up, and the ones that
 * are not a single number.
 *
 * The budget half of this screen answers "what does the subtree add up to". This
 * half answers "what is this vehicle", and almost nothing about it is the same
 * shape. A centre of gravity is three numbers that are meaningless apart. An
 * inertia tensor is six, about a point that is three more, and the point is the
 * difference between a tensor somebody can use and a tensor nobody can. Delta-V,
 * envelope volume and pointing accuracy are single figures that deliberately do
 * NOT aggregate - a vehicle's delta-V is a function of its mass ratio, allocated
 * downward, which is the opposite direction from a rollup.
 *
 * Three things this module is careful about, each of which has a wrong answer
 * that looks exactly like a right one:
 *
 *   - **A usage carries nothing.** Values are stated on the DEFINITION, so
 *     `GET /api/parts/vehicle` answers null for every attribute and names `SYS`
 *     as its definition. A screen that read the usage would report a fully
 *     specified vehicle as entirely blank. `definitionOf` is the whole fix and
 *     it is the same rule the budgets follow.
 *   - **Grouping comes from the FEATURE PATH, never from the field name.** The
 *     model wrote `centerOfGravity.x` and `inertia.aboutPoint.x`; that path is
 *     the model's own statement that these are components of one thing. Reading
 *     it off `cg_` and `inertia_` prefixes would work today and would be a
 *     parallel list the model has to wait on - the exact coupling that storing
 *     values by feature path removed.
 *   - **A value is text with a unit.** `'310 [SI::kg⋅m²]'`, not `310`. Nothing
 *     here converts one unit to another, and nothing infers a unit that is not
 *     written. A quantity in an unexpected unit is shown as written.
 */

import type { FieldSchema, KindSchema, StatedValues } from '../../schema'
import { unitLabel } from './budget-model'

/**
 * A quantity as stored, split into the number and the unit as WRITTEN.
 *
 * Split for display only - to set the unit in quieter type beside the figure -
 * and never to compare two values or to convert one. `text` is what the model
 * says and is always safe to show; `value` is null wherever the stored text is
 * not a bare number followed by a unit, which is not an error and is why `text`
 * survives alongside it.
 */
export interface StatedQuantity {
  /** Exactly what the model stores, e.g. `310 [SI::kg⋅m²]`. */
  text: string
  /** The numeric part, or null where the text is not a plain quantity. */
  value: number | null
  /** The unit as a person writes it, e.g. `kg⋅m²`. `''` when none is written. */
  unit: string
}

/** `'310 [SI::kg⋅m²]'` and `'0.42 [SI::m³]'`, and nothing more elaborate. */
const QUANTITY = /^\s*(-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?)\s*(?:\[([^\]]*)\])?\s*$/

/**
 * One stored value, read for display.
 *
 * @param stored the value as the payload answers it, or null/undefined if unset
 * @returns the quantity, or null when nothing is stated - which is NOT zero
 */
export function readQuantity(stored: unknown): StatedQuantity | null {
  if (stored === null || stored === undefined) return null
  const text = String(stored).trim()
  if (!text) return null

  const match = QUANTITY.exec(text)
  // NOT A FAILURE. A value BEST cannot split is still a value the model states,
  // so it is reported verbatim rather than dropped or shown as unset - the same
  // rule the rollup follows when it declines to sum something.
  if (!match) return { text, value: null, unit: '' }
  return {
    text,
    value: Number(match[1]),
    unit: match[2] ? unitLabel(match[2]) : '',
  }
}

/** One leaf of a structured attribute - a tensor term, a vector component. */
export interface AttributeLeaf {
  /** The registry key, e.g. `inertia_ixx`. */
  name: string
  /** The feature path the model wrote, e.g. `inertia.aboutPoint.x`. */
  feature: string
  /**
   * What to call it WITHIN its group - `ixx`, `x`, `aboutPoint.x`.
   *
   * The group's own name is stripped, because "Inertia Ixx" repeated six times
   * under a heading that already says Inertia is a table nobody can scan.
   */
  label: string
  /** The declared unit, from the registry, whether or not a value is stated. */
  unit: string
  help: string
  /** What the model states, or null when it states nothing. */
  quantity: StatedQuantity | null
}

/**
 * One attribute of the system - a single figure, or a structure of them.
 *
 * A scalar is a group of exactly one leaf whose label is empty, deliberately:
 * one shape for the caller rather than a union it has to branch on, and the
 * difference a reader cares about (is this one number or nine?) is `leaves`.
 */
export interface AttributeGroup {
  /** The feature path's first segment - `inertia`, `dryMass`. */
  feature: string
  /** The heading - `Inertia`, `CoG`, `Delta-V`. */
  label: string
  leaves: AttributeLeaf[]
  /** True where the model declares more than one leaf under this feature. */
  isStructured: boolean
  /** True where at least one leaf carries a value. */
  isStated: boolean
}

/**
 * A group's heading, from the labels of the leaves the model put under it.
 *
 * The longest COMMON PREFIX of the leaf labels, which is what the profile's own
 * generated labels give: `Inertia Ixx`/`Inertia Iyy`/… yields `Inertia`, and
 * `CoG x`/`CoG y`/`CoG z` yields `CoG`. Derived rather than tabulated, so a
 * structure added to the library gets a heading with nothing here being edited -
 * and it falls back to the feature name when the labels share nothing, which is
 * a heading a person can still read.
 */
export function groupLabel(feature: string, labels: readonly string[]): string {
  if (labels.length === 1) return labels[0]
  const words = labels.map((label) => label.split(/\s+/))
  const shortest = Math.min(...words.map((parts) => parts.length))
  const common: string[] = []
  for (let at = 0; at < shortest; at += 1) {
    const word = words[0][at]
    if (!words.every((parts) => parts[at] === word)) break
    common.push(word)
  }
  return common.length > 0 ? common.join(' ') : feature
}

/** Everything after the group's own segment - `ixx`, `aboutPoint.x`, `x`. */
function leafLabel(feature: string, label: string, groupHeading: string): string {
  const trimmed = label.startsWith(groupHeading)
    ? label.slice(groupHeading.length).trim()
    : label
  // The feature's own remainder is the better name where stripping the heading
  // leaves nothing - a one-leaf group, which is a scalar and wants no sub-label.
  return trimmed || feature.split('.').slice(1).join('.')
}

/**
 * Every quantity attribute the registry declares, grouped as the model wrote it.
 *
 * Driven by `kind.fields` - the registry's own description of a part - and
 * grouped by the first segment of each field's feature path. WHICH attributes
 * exist is therefore the model's answer, not a list here: an attribute added to
 * the library appears with a heading, a unit and a place on the page without
 * this file being touched, which is the entire point of storing values by
 * feature path.
 *
 * `skip` is how the caller removes what is already on screen elsewhere - the
 * rolling quantities, which have tiles and charts of their own. They are omitted
 * rather than repeated, because the same number twice in two shapes reads as two
 * different numbers.
 *
 * @param kind the `parts` entry from `GET /api/schema`
 * @param stated the definition's payload - NOT a usage, which states nothing
 * @param skip field names to leave out, e.g. the rolling attributes' keys
 */
export function attributeGroups(
  kind: KindSchema | null | undefined,
  stated: StatedValues | null | undefined,
  skip: readonly string[] = [],
): AttributeGroup[] {
  const omitted = new Set(skip)
  const quantities = (kind?.fields ?? []).filter(
    (field: FieldSchema) =>
      field.kind === 'quantity' && field.feature && !omitted.has(field.name),
  )

  // Declaration order, which is the profile's order, which is the order a
  // person reading the library meets them in.
  const order: string[] = []
  const byFeature = new Map<string, FieldSchema[]>()
  for (const field of quantities) {
    const feature = field.feature.split('.')[0]
    if (!byFeature.has(feature)) {
      byFeature.set(feature, [])
      order.push(feature)
    }
    byFeature.get(feature)?.push(field)
  }

  return order.map((feature) => {
    const fields = byFeature.get(feature) ?? []
    const heading = groupLabel(feature, fields.map((field) => field.label))
    const leaves = fields.map((field) => ({
      name: field.name,
      feature: field.feature,
      label: leafLabel(field.feature, field.label, heading),
      unit: field.unit,
      help: field.help,
      quantity: readQuantity(stated?.[field.name]),
    }))
    return {
      feature,
      label: heading,
      leaves,
      isStructured: leaves.length > 1,
      isStated: leaves.some((leaf) => leaf.quantity !== null),
    }
  })
}

/**
 * The groups worth showing, and the count of the ones left out.
 *
 * A system states a handful of its thirty-odd attributes, so a table of thirty
 * rows reading `not stated` buries the four that say something. The unstated
 * ones are COUNTED rather than dropped: "18 more state nothing" is a fact about
 * the model and an invitation to fill them in, where silence reads as a screen
 * that only knows about four.
 */
export function statedGroups(groups: readonly AttributeGroup[]): {
  stated: AttributeGroup[]
  unstated: AttributeGroup[]
} {
  return {
    stated: groups.filter((group) => group.isStated),
    unstated: groups.filter((group) => !group.isStated),
  }
}
